import React, { FC, useEffect, useReducer } from 'react';

import { RuleCreate } from '@ariksa/compliance-policies/api';
import { Box, Center, Flex, HStack, Stack, Textarea } from '@chakra-ui/react';
import { isEmpty, replace, startCase } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { Card, PageHeaderWithIcon } from 'components/DataDisplay';
import { LearnMore } from 'components/DataDisplay/KnowledgeBase/Components/LearnMore';
import { useKnowledgeBase } from 'components/DataDisplay/KnowledgeBase/hook/useKnowledgeBase';
import { CustomSpinner } from 'components/DataDisplay/Spinner/CustomSpinner';
import { severityOptions } from 'components/DataDisplay/Utils/renderSeverity';
import {
  Form,
  inlineObjectFieldStyles,
  OutlinedIconButton,
  PrimaryButton,
  textAreaFieldStyles,
} from 'components/DataEntry';
import { DocumentsIcon, PolicyIcon } from 'components/Icons';
import { policySeverityOptions } from 'containers/PolicyHub/Policies/CreatePolicy/utils';
import { selectPolicyHub } from 'containers/PolicyHub/selectors';
import { actions } from 'containers/PolicyHub/slice';
import { useSearchParams } from 'hooks/useSearchParams';

export const CreatePolicy: FC = () => {
  const {
    searchQuery,
    saveQuery,
    ruleCategories,
    generatePolicy,
  } = useSelector(selectPolicyHub);
  const { setLearnMore } = useKnowledgeBase();
  const params = useSearchParams<{ query: string; resource: string }>();

  const [state, updateState] = useReducer(
    (prev, next) => {
      return { ...prev, ...next };
    },
    {
      issueCategoryOptions: [],
      issueCategory: {},
      severity: policySeverityOptions[0],
      description: '',
      issueDescription: '',
      query: '',
    },
  );

  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    if (!!params.query) updateState({ query: params.query });
  }, [params.query]);

  useEffect(() => {
    dispatch(
      actions.getRuleCategories({
        q: {},
      }),
    );
  }, [dispatch]);

  useEffect(() => {
    if (isEmpty(ruleCategories.data)) return;
    const options = ruleCategories.data.map(e => ({
      label: startCase(e.toLowerCase()),
      value: e,
    }));
    updateState({ issueCategoryOptions: options, issueCategory: options?.[0] });
  }, [ruleCategories.data]);

  const handleGeneratePolicy = () => {
    dispatch(
      actions.generatePolicy({
        q: {
          policyQueryRequest: {
            intent: state.description,
          },
        },
        onSuccess: res => {
          updateState({ query: res?.query });
        },
      }),
    );
  };

  const handleSubmit = data => {
    const { category, issueDescription } = data;
    const { issueCategory = {}, severity = {} } = category ?? {};

    // first save the query
    const policy: RuleCreate = {
      config_id: replace(state?.query, /[ =/]/g, '_'),
      is_enabled: true,
      search_id: '',
      //compliance_description: description,
      alert_description: issueDescription,
      alert_category: issueCategory.value,
      //category_class: categoryClass.value,
      severity: severity.value,
      //resource_type: searchQuery.resource?.name,
      //provider: state.provider?.value,
    };

    dispatch(
      actions.saveQuery({
        q: {
          searchWrapperInput: {
            wrapper: {
              resource: params?.resource,
            },
            query: state.query,
          },
        },
        onSuccess: res => {
          if (!res?.id) {
            console.error('failed to save search query');
          }

          policy.search_id = res?.id;
          dispatch(
            actions.createPolicy({
              q: { ruleCreate: policy },
              onSuccess: () => {
                navigate(`/policy-hub/policy/cloud`);
              },
            }),
          );
        },
      }),
    );
  };

  return (
    <Card
      contentComponent={
        <Box pl={32} ml={8}>
          <Form
            title={
              <PageHeaderWithIcon
                label={'Create Policy'}
                icon={<PolicyIcon />}
              />
            }
            schema={{
              description: {
                type: 'custom-with-form-control',
                label: (
                  <HStack w="full" justify="space-between">
                    <Box>Describe what you are trying to detect</Box>
                    <OutlinedIconButton
                      color="primary"
                      leftIcon={<DocumentsIcon color="primary" />}
                      onClick={() => setLearnMore('create_policy')}
                      textDecoration="dotted"
                    >
                      How does this work?
                    </OutlinedIconButton>
                  </HStack>
                ),
                isRequired: true,
                value: state.name,
                component: (
                  <Stack w="full">
                    <Textarea
                      {...textAreaFieldStyles.textArea}
                      onChange={e =>
                        updateState({ description: e.target.value })
                      }
                      placeholder="Describe your policy"
                      value={state.description}
                    ></Textarea>
                    <Flex justify="flex-end">
                      <Box>
                        <PrimaryButton onClick={handleGeneratePolicy}>
                          GENERATE POLICY
                        </PrimaryButton>
                      </Box>
                    </Flex>
                  </Stack>
                ),
              },
              generatedPolicy: {
                type: 'custom-with-form-control',
                label: 'Policy generated by Ariksa AI',
                component: (
                  <Box
                    border="1px solid"
                    borderColor="componentBorder"
                    borderRadius={6}
                    px={4}
                    py={4}
                    h={64}
                  >
                    {generatePolicy.isLoading ? (
                      <Center w="full" h="full">
                        <CustomSpinner />
                      </Center>
                    ) : (
                      state?.query
                    )}
                  </Box>
                ),
              },
              issueDescription: {
                type: 'textArea',
                placeholder:
                  'Provide a description that is easy to understand for anyone',
                label: 'Issue Description',
                value: state.issueDescription,
                onChange: val => updateState({ issueDescription: val }),
              },
              category: {
                type: 'object',
                properties: {
                  issueCategory: {
                    type: 'react-select',
                    label: 'Issue category',
                    options: state.issueCategoryOptions,
                    value: state.issueCategory,
                    onChange: val => updateState({ issueCategory: val }),
                  },
                  severity: {
                    type: 'react-select',
                    label: 'Severity',
                    options: severityOptions,
                    value: state.severity,
                    onChange: val => updateState({ severity: val }),
                  },
                },
              },
            }}
            buttonOptions={{
              submit: {
                name: 'OK',
                isLoading: saveQuery.isLoading,
                isDisabled:
                  saveQuery.isLoading ||
                  !state.issueDescription ||
                  !state.query,
              },
              reset: {
                name: 'CANCEL',
                isVisible: true,
                onClick: () => navigate('/policy-hub/policy/cloud'),
              },
            }}
            handleSubmit={handleSubmit}
            styles={{
              form: { formWidth: '800px', submitButton: { px: 6 } },
              objectField: {
                ...inlineObjectFieldStyles,
                propertyContainer: [{ w: '60%' }, { w: '40%' }],
              },
            }}
          />
          <LearnMore />
        </Box>
      }
    />
  );
};
